import type { AvailabilitySlot } from '@gh/common/models'
import createLogger from '@/utils/logger'

/**
 * Dedicated debug logger for clinical booking calendar consultant filtering
 * (licensed-states cache, then insured Soft PE intersect).
 *
 * Enable in LoggerConfig: namespace `clinical-booking-calendar-consultant-filter`.
 * In Vite DEV, also prints to the browser console without toggling LoggerConfig.
 */
export const CLINICAL_CALENDAR_CONSULTANT_FILTER_LOG_NS =
  'clinical-booking-calendar-consultant-filter'

const logger = createLogger(CLINICAL_CALENDAR_CONSULTANT_FILTER_LOG_NS)

const lastFingerprintByStage = new Map<string, string>()

export function uniqueSortedIds(ids: Iterable<string>): string[] {
  return [...new Set(ids)].sort()
}

export type SlotConsultantInsuranceEligibility =
  | 'self-pay'
  | 'pe-mapped'
  | 'pe-not-mapped'
  | 'passthrough-empty-mapped'
  | 'pe-error'
  | 'pe-loading'

export type SlotConsultantInsuranceRow = {
  tellescopeUserId: string
  consultantId?: string
  bridgeProviderId?: string
  isSelectedPick: boolean
  insuranceEligible: boolean
  eligibility: SlotConsultantInsuranceEligibility
}

export type MappedBookableProvider = {
  consultantId: string
  tellescopeUserId: string
  bridgeProviderId: string
}

function insuranceEligibilityForUser(args: {
  insured: boolean
  peLoading: boolean
  peError: string | null
  mappedIds: Set<string> | null
  tellescopeUserId: string
}): { insuranceEligible: boolean; eligibility: SlotConsultantInsuranceEligibility } {
  if (!args.insured) {
    return { insuranceEligible: false, eligibility: 'self-pay' }
  }
  if (args.peLoading && !args.mappedIds) {
    return { insuranceEligible: false, eligibility: 'pe-loading' }
  }
  if (args.peError) {
    return { insuranceEligible: false, eligibility: 'pe-error' }
  }
  if (!args.mappedIds || args.mappedIds.size === 0) {
    return { insuranceEligible: true, eligibility: 'passthrough-empty-mapped' }
  }
  if (args.mappedIds.has(args.tellescopeUserId)) {
    return { insuranceEligible: true, eligibility: 'pe-mapped' }
  }
  return { insuranceEligible: false, eligibility: 'pe-not-mapped' }
}

export function buildSelectedSlotConsultantFilterPayload(args: {
  slot: AvailabilitySlot
  insured: boolean
  visitState: string | null
  peLoading: boolean
  peError: string | null
  mappedIds: Set<string> | null
  mappedProviders: MappedBookableProvider[]
  consultantMap: Record<string, string>
  licensedSlots: Array<{ userId: string; startTimeInMS: number; durationInMinutes: number }>
}): Record<string, unknown> {
  const startTimeInMS = args.slot.startTime.getTime()
  const durationMinutes = args.slot.duration
  const selectedTellescopeUserId = args.slot.userIds[0]
  const providerByTellescopeUserId = new Map(
    args.mappedProviders.map((provider) => [provider.tellescopeUserId, provider]),
  )
  const consultantsOnSlot = uniqueSortedIds([
    ...args.licensedSlots
      .filter(
        (block) =>
          block.startTimeInMS === startTimeInMS && block.durationInMinutes === durationMinutes,
      )
      .map((block) => block.userId),
    ...args.slot.userIds,
  ])

  const consultants: SlotConsultantInsuranceRow[] = consultantsOnSlot.map((tellescopeUserId) => {
    const eligibility = insuranceEligibilityForUser({
      insured: args.insured,
      peLoading: args.peLoading,
      peError: args.peError,
      mappedIds: args.mappedIds,
      tellescopeUserId,
    })
    const mapped = providerByTellescopeUserId.get(tellescopeUserId)
    return {
      tellescopeUserId,
      consultantId: mapped?.consultantId ?? args.consultantMap[tellescopeUserId],
      bridgeProviderId: mapped?.bridgeProviderId,
      isSelectedPick: tellescopeUserId === selectedTellescopeUserId,
      insuranceEligible: eligibility.insuranceEligible,
      eligibility: eligibility.eligibility,
    }
  })

  const insuranceEligibleConsultants = consultants.filter((row) => row.insuranceEligible)
  const selectedRow = consultants.find((row) => row.isSelectedPick)

  return {
    slotId: args.slot.id,
    startTime: args.slot.startTime.toISOString(),
    endTime: args.slot.endTime.toISOString(),
    durationMinutes,
    available: args.slot.available,
    visitState: args.visitState,
    insured: args.insured,
    peLoading: args.peLoading,
    peError: args.peError,
    peFilterApplied: Boolean(args.mappedIds && args.mappedIds.size > 0),
    selectedTellescopeUserId,
    selectedConsultantId: selectedRow?.consultantId,
    selectedIsInsuranceEligible: selectedRow?.insuranceEligible ?? false,
    consultantCount: consultants.length,
    insuranceEligibleCount: insuranceEligibleConsultants.length,
    insuranceIneligibleCount: consultants.length - insuranceEligibleConsultants.length,
    consultants,
    insuranceEligibleConsultants,
  }
}

export function logClinicalCalendarConsultantFilter(
  stage: string,
  payload: Record<string, unknown>,
  options?: { alwaysEmit?: boolean },
): void {
  const fingerprint = JSON.stringify(payload)
  if (!options?.alwaysEmit && lastFingerprintByStage.get(stage) === fingerprint) {
    return
  }
  lastFingerprintByStage.set(stage, fingerprint)

  const data = { stage, ...payload }
  if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
    // eslint-disable-next-line no-console
    console.debug('[clinicalCalendarConsultantFilter]', data)
  }
  logger.info(`[clinicalCalendarConsultantFilter] ${stage}`, data)
}
